import type { SubsPlanType } from '@/app/types';
import type { PlanRight } from './types';

/**
 * 个人版套餐 plan_type。team_v2 走独立的 TeamPlanCard，不出现在以下常量里。
 */
export type IndividualPlanType = Exclude<SubsPlanType, 'team_v2'>;

export const PLAN_TITLE_TYPE_MAP: Record<IndividualPlanType, React.CSSProperties> = {
  free: {
    color: '#fff',
  },
  standard_v2: {
    backgroundImage: 'linear-gradient(90deg, #CFE7FF 0%, #79A4FF 100%)',
  },
  premium_v2: {
    backgroundImage: 'linear-gradient(91deg, #FAF1C8 3.96%, #BDA16C 96.78%)',
  },
  ultimate_v2: {
    backgroundImage: 'linear-gradient(90deg, #E0DDFF 2.78%, #B494F2 95.83%)',
  },
};

// 积分list
export const CREDITS_CONFIG_LIST: Record<
  IndividualPlanType,
  {
    credits: number | string;
    videos: number | string;
    bgImg: string;
  }
> = {
  standard_v2: {
    credits: 800,
    videos: 200,
    bgImg: 'bg-[linear-gradient(270deg,#78A3FF,#D0E8FF)]',
  },
  premium_v2: {
    credits: 4000,
    videos: 1000,
    bgImg: 'bg-[linear-gradient(270deg,#BA9D64,#FFF7D0)]',
  },
  ultimate_v2: {
    credits: 8000,
    videos: 2000,
    bgImg: 'bg-[linear-gradient(270deg,#B1A7FF,#D6D0FF)]',
  },
  free: {
    credits: 0,
    videos: 0,
    bgImg: '',
  },
};

// 活动开放后专业版展示 3500 月度积分，旧配置保留用于活动前展示。
export const NEW_MEMBERSHIP_CREDITS_CONFIG_LIST = {
  ...CREDITS_CONFIG_LIST,
  premium_v2: { ...CREDITS_CONFIG_LIST.premium_v2, credits: 3500 },
};

// 活动开放后的旗舰版两档展示积分，同周期商品按原价从低到高对应。
export const NEW_MEMBERSHIP_ULTIMATE_CREDITS = { base: 10_000, higher: 20_000 } as const;

/** Q4 每秒视频生成的分辨率维度，卡片按此顺序展示。 */
export const Q4_CONSUMPTION_RESOLUTIONS = ['540P', '720P', '1080P', '2K', '4K'] as const;

/**
 * Q4 模型每秒消耗积分（实际每秒消耗积分列）：
 * - free 走「标准刊例价」列（不打折）；
 * - 所有会员档位统一使用 540P 5 折、其他清晰度 6 折，不区分购买周期。
 */
export const Q4_CONSUMPTION_CONFIG: Record<
  IndividualPlanType,
  Record<(typeof Q4_CONSUMPTION_RESOLUTIONS)[number], number>
> = {
  free: { '540P': 4, '720P': 8, '1080P': 10, '2K': 20, '4K': 30 },
  standard_v2: { '540P': 2, '720P': 5, '1080P': 6, '2K': 12, '4K': 18 },
  premium_v2: { '540P': 2, '720P': 5, '1080P': 6, '2K': 12, '4K': 18 },
  ultimate_v2: { '540P': 2, '720P': 5, '1080P': 6, '2K': 12, '4K': 18 },
};

/**
 * 试用期积分与视频数量配置：试用 tab 下各档位的积分/视频上限。
 * - Standard: 350 积分 ≈ 88 视频
 * - Premium: 900 积分 ≈ 225 视频
 * - Ultimate: 1650 积分 ≈ 412 视频
 */
export const TRIAL_CREDITS_CONFIG: Record<
  'standard_v2' | 'premium_v2' | 'ultimate_v2',
  { credits: number; videos: number }
> = {
  standard_v2: { credits: 350, videos: 88 },
  premium_v2: { credits: 900, videos: 225 },
  ultimate_v2: { credits: 1650, videos: 412 },
};

export const MAX_PLAN_VIDEOS = Math.max(
  ...Object.entries(CREDITS_CONFIG_LIST)
    .filter(([key]) => key !== 'free')
    .map(([, v]) => (typeof v.videos === 'number' ? v.videos : 0)),
);

/**
 * 各档"核心权益"列表（紫色 icon + UNLIMITED 风格）。
 */
export const PLAN_HIGHLIGHTED_RIGHTS: Record<IndividualPlanType, readonly PlanRight[]> = {
  free: [],
  standard_v2: ['fastSpeedRight', 'agentBasicRight'],
  premium_v2: ['highSpeedRight', 'agentMoreRight'],
  ultimate_v2: ['ultraFastSpeedRight', 'agentRight'],
};

export const NEW_MEMBERSHIP_HIGHLIGHTED_RIGHTS: Record<IndividualPlanType, readonly PlanRight[]> = {
  free: [],
  standard_v2: ['fastSpeedRight'],
  premium_v2: ['ultraFastSpeedRight'],
  ultimate_v2: ['ultraFastSpeedRight'],
};

/** 各档"普通权益"列表（灰色 check 风格）。 */
export const PLAN_COMMON_RIGHTS: Record<IndividualPlanType, readonly PlanRight[]> = {
  free: ['referenceFreeRight'],
  standard_v2: ['durationRight', 'qualityRight', 'modeRight', 'proMode', 'encodingFormatRight', 'commercialUse'],
  premium_v2: ['durationRight', 'qualityRight', 'modeRight', 'proMode', 'encodingFormatRight', 'commercialUse'],
  ultimate_v2: ['durationRight', 'qualityRight', 'modeRight', 'proMode', 'encodingFormatRight', 'commercialUse'],
};

/** 国内站达人合作码文案（仅 SITE=china 使用，无需走 i18n） */
export const INFLUENCER_CODE_TEXT = {
  dialog: {
    error: '提交失败，请检查合作码是否正确',
    placeholder: '请填写达人合作码',
    submit: '提交',
    success: '提交成功，下单支持你心仪的达人吧～',
    title: '达人合作码',
  },
  entry: {
    coopCode: '达人合作码',
  },
  paymentError: '达人合作码错误/失效',
} as const;
